// Parser fuer EPC-QR (auch „Girocode") sowie generische QR-Inhalte.
// EPC-QR-Format (Service-To-User v2):
//   Zeile 1: BCD                (Service Tag)
//   Zeile 2: Version (001/002)
//   Zeile 3: Encoding (1=UTF-8)
//   Zeile 4: SCT                (Identification)
//   Zeile 5: BIC                (optional)
//   Zeile 6: Empfaengername
//   Zeile 7: IBAN
//   Zeile 8: Betrag             z. B. "EUR123.45"
//   Zeile 9: Purpose-Code       (optional)
//   Zeile 10: Strukturierte Referenz (RF…)
//   Zeile 11: Verwendungszweck  (unstrukturiert)
//   Zeile 12: Information       (optional)

export interface EpcParseResult {
  raw: string;
  /** Erkannter Gesamtbetrag in EUR, oder null falls nicht ableitbar. */
  total: number | null;
  /** Verwendungszweck-String fuer das spaetere Matching auf RecordNumber. */
  purpose: string;
  /** Empfaengername falls vorhanden. */
  recipient: string;
  /** IBAN ohne Leerzeichen, falls vorhanden. */
  iban: string;
  /** BIC falls vorhanden. */
  bic: string;
  /** True, wenn das QR-Format einem EPC-/Girocode-Block entspricht. */
  isEpc: boolean;
}

const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{12,30}$/;

function parseAmountLine(line: string): number | null {
  // EPC: "EUR123.45". Wir akzeptieren tolerant auch "EUR 123,45" oder
  // ein leerer Betrag (kommt bei Lastschrift-QR ohne Faelligkeitsbetrag vor).
  const m = line.trim().match(/^([A-Z]{3})\s*([0-9.,]+)?$/);
  if (!m) return null;
  const amountStr = (m[2] ?? '').replace(/\s/g, '').replace(',', '.');
  if (!amountStr) return null;
  const num = Number.parseFloat(amountStr);
  return Number.isFinite(num) ? num : null;
}

export function parseQrPayload(rawInput: string): EpcParseResult {
  const raw = rawInput ?? '';
  // EPC-Encoder verwenden gerne CR/LF gemischt — wir splitten beide.
  const lines = raw.split(/\r\n|\r|\n/);
  const trimmed = lines.map(l => l.trim());
  const isEpc =
    trimmed.length >= 6 &&
    /^BCD$/i.test(trimmed[0]) &&
    /^SCT$/i.test(trimmed[3]);

  if (isEpc) {
    const bic = trimmed[4] ?? '';
    const recipient = trimmed[5] ?? '';
    const iban = (trimmed[6] ?? '').replace(/\s+/g, '').toUpperCase();
    const total = parseAmountLine(trimmed[7] ?? '');
    // Verwendungszweck: bevorzugt Zeile 11 (unstrukturiert), sonst 10
    // (strukturierte Referenz) – wer keinen Verwendungszweck setzt, hat
    // beides leer.
    const unstructured = (trimmed[10] ?? '').trim();
    const structured = (trimmed[9] ?? '').trim();
    const purpose = unstructured || structured;
    return {
      raw,
      total,
      purpose,
      recipient,
      iban: IBAN_RE.test(iban) ? iban : '',
      bic,
      isEpc: true,
    };
  }

  // Fallback: kein EPC-QR. Wir behalten das alte Verhalten bei – letzte
  // nicht-leere Zeile als Verwendungszweck nehmen; Betrag heuristisch aus
  // einer eventuellen "EUR …"-Zeile lesen.
  const nonEmpty = trimmed.filter(l => l.length > 0);
  const purpose = nonEmpty.length > 0 ? nonEmpty[nonEmpty.length - 1] : '';
  let total: number | null = null;
  for (const l of nonEmpty) {
    const t = parseAmountLine(l);
    if (t !== null) {
      total = t;
      break;
    }
  }
  return {
    raw,
    total,
    purpose,
    recipient: '',
    iban: '',
    bic: '',
    isEpc: false,
  };
}
